import React, { useState, useEffect } from 'react';
import { Printer, X } from 'lucide-react';

interface PrintQuantityDialogProps {
  isOpen: boolean;
  productName: string;
  defaultQuantity?: number;
  onConfirm: (quantity: number) => void;
  onCancel: () => void;
}

export const PrintQuantityDialog: React.FC<PrintQuantityDialogProps> = ({
  isOpen,
  productName,
  defaultQuantity = 66,
  onConfirm,
  onCancel,
}) => {
  const [quantity, setQuantity] = useState<number>(defaultQuantity);

  useEffect(() => {
    if (isOpen) {
      setQuantity(defaultQuantity);
    }
  }, [isOpen, defaultQuantity]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (quantity > 0 && quantity <= 1000) {
      onConfirm(quantity);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full border border-gray-200 dark:border-gray-700 animate-in zoom-in duration-200">
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-full flex-shrink-0 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
              <Printer size={21} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                Címke mennyiség megadása
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                <span className="font-medium">{productName}</span>
              </p>
              <form onSubmit={handleSubmit}>
                <div className="mb-6">
                  <label
                    htmlFor="quantity-input"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Hány darab címkét szeretnél nyomtatni?
                  </label>
                  <input
                    id="quantity-input"
                    type="number"
                    min="1"
                    max="1000"
                    value={quantity}
                    onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                    onFocus={(e) => e.target.select()}
                    onKeyDown={handleKeyDown}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent dark:bg-gray-700 dark:text-white text-lg font-semibold"
                    autoFocus
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    Egy ív 66 db címkét tartalmaz (6 oszlop × 11 sor, 48×25mm)
                  </p>
                </div>
                <div className="flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={onCancel}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg font-medium transition"
                  >
                    Mégse
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg font-medium transition"
                  >
                    Hozzáadás ({quantity} db)
                  </button>
                </div>
              </form>
            </div>
            <button
              onClick={onCancel}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition p-1"
              aria-label="Bezárás"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
